import React from "react";
import { Link } from "react-router-dom";

import { useMe, usePocketPlaySurvey, useSavePocketPlaySurvey } from "../api/api_hooks";
import { getSurveyContent } from "../survey/surveyContent";


const SECTION_COLORS = [
  "from-cyan-400 to-sky-500", "from-violet-400 to-fuchsia-500", "from-pink-400 to-rose-500",
  "from-amber-300 to-orange-500", "from-emerald-300 to-cyan-500", "from-blue-400 to-indigo-500",
  "from-fuchsia-400 to-pink-500", "from-cyan-300 to-violet-500", "from-amber-300 to-pink-500", "from-emerald-300 to-sky-500",
];

const textAreaClass = "mt-4 min-h-28 w-full resize-y rounded-2xl border border-white/10 bg-slate-950/55 px-4 py-3 text-sm leading-7 text-white outline-none transition placeholder:text-indigo-200/30 focus:border-cyan-300/50 focus:ring-4 focus:ring-cyan-400/10 disabled:cursor-not-allowed disabled:opacity-65";

function Question({ number, title, hint, children }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-slate-950/35 p-5 shadow-xl shadow-slate-950/15 sm:p-6">
      <div className="flex items-start gap-4">
        <span className="grid h-9 min-w-9 place-items-center rounded-xl bg-white/10 text-sm font-black text-cyan-200 ring-1 ring-white/10">{number}</span>
        <div className="min-w-0 flex-1 text-start">
          <h3 className="survey-title-font text-base font-extrabold leading-7 text-white sm:text-lg">{title}</h3>
          {hint ? <p className="mt-1 text-sm leading-6 text-indigo-100/55">{hint}</p> : null}
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function RadioChoices({ name, options, value, onChange, disabled }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" role="radiogroup">
      {options.map((choice) => {
        const checked = value === choice.value;
        return (
          <label key={choice.value} className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-start text-sm font-semibold transition ${checked ? "border-cyan-300/50 bg-cyan-400/15 text-white shadow-lg shadow-cyan-950/20" : "border-white/10 bg-white/[0.035] text-indigo-100/75 hover:border-white/20 hover:bg-white/[0.06]"} ${disabled ? "cursor-not-allowed opacity-65" : ""}`}>
            <input className="h-4 w-4 accent-cyan-300" type="radio" name={name} value={choice.value} checked={checked} onChange={() => onChange(choice.value)} disabled={disabled} />
            <span>{choice.label}</span>
          </label>
        );
      })}
    </div>
  );
}

function CheckboxChoices({ options, values = [], onChange, disabled, max }) {
  const toggle = (choice) => {
    if (values.includes(choice)) {
      onChange(values.filter((value) => value !== choice));
    } else if (!max || values.length < max) {
      onChange([...values, choice]);
    }
  };
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {options.map((choice) => {
        const checked = values.includes(choice.value);
        const limitReached = Boolean(max && values.length >= max && !checked);
        return (
          <label key={choice.value} className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-start text-sm font-semibold transition ${checked ? "border-pink-300/50 bg-pink-400/15 text-white" : "border-white/10 bg-white/[0.035] text-indigo-100/75"} ${disabled || limitReached ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:border-white/20 hover:bg-white/[0.06]"}`}>
            <input className="h-4 w-4 rounded accent-pink-400" type="checkbox" checked={checked} onChange={() => toggle(choice.value)} disabled={disabled || limitReached} />
            <span>{choice.label}</span>
          </label>
        );
      })}
    </div>
  );
}

function ScoreSlider({ value, onChange, min, max, minLabel, maxLabel, disabled, label }) {
  const current = value ?? Math.round((min + max) / 2);
  const percentage = ((current - min) / (max - min)) * 100;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.035] px-4 py-4 sm:px-5">
      <div className="mb-3 flex items-center justify-between gap-4">
        {label ? <span className="text-sm font-bold text-indigo-100/75">{label}</span> : <span />}
        <output className="grid h-11 min-w-11 place-items-center rounded-2xl bg-gradient-to-br from-cyan-300 to-violet-400 px-3 text-lg font-black text-slate-950 shadow-lg shadow-cyan-950/25">{current}</output>
      </div>
      <input
        className="survey-range h-2 w-full cursor-pointer appearance-none rounded-full bg-white/10 accent-cyan-300 disabled:cursor-not-allowed disabled:opacity-60"
        style={{ "--survey-progress": `${percentage}%` }}
        dir="ltr"
        type="range" min={min} max={max} step="1" value={current}
        onChange={(event) => onChange(Number(event.target.value))} disabled={disabled}
        aria-label={label}
      />
      <div dir="ltr" className="mt-3 flex justify-between gap-4 text-xs font-semibold text-indigo-100/50">
        <span>{min} — {minLabel}</span><span>{max} — {maxLabel}</span>
      </div>
    </div>
  );
}

function TextAnswer({ value = "", onChange, placeholder, disabled, rows = 4 }) {
  return <textarea className={textAreaClass} value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} disabled={disabled} rows={rows} />;
}

function Section({ index, title, description, children }) {
  return (
    <section id={`survey-section-${index + 1}`} className="scroll-mt-28 overflow-hidden rounded-[2rem] border border-white/10 bg-indigo-950/45 shadow-2xl shadow-slate-950/30 backdrop-blur-xl">
      <div className="relative overflow-hidden border-b border-white/10 px-5 py-6 sm:px-7">
        <div className={`absolute inset-y-0 start-0 w-1.5 bg-gradient-to-b ${SECTION_COLORS[index]}`} />
        <p className="text-xs font-black uppercase tracking-[0.22em] text-indigo-200/45">{index + 1} / 10</p>
        <h2 className="survey-title-font mt-2 text-2xl font-black text-white sm:text-3xl">{title}</h2>
        <p className="mt-2 max-w-3xl text-sm leading-7 text-indigo-100/60">{description}</p>
      </div>
      <div className="grid gap-5 p-4 sm:p-6">{children}</div>
    </section>
  );
}

function LoadingCard() {
  return (
    <main className="flex flex-grow items-center justify-center px-4 py-20">
      <div className="rounded-3xl border border-white/10 bg-indigo-950/50 px-10 py-8 text-indigo-100/70 shadow-2xl">
        <span className="me-3 inline-block h-3 w-3 animate-pulse rounded-full bg-cyan-300" />Loading…
      </div>
    </main>
  );
}

function visibleQuestionNumbers(answers) {
  const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 15, 16, 17, 20, 22, 23, 24, 25, 26, 27, 28];
  if (answers.q11 === "yes") numbers.push(12, 13, 14);
  if (answers.q17 === "no") numbers.push(18);
  if (answers.q17 === "no" && answers.q18 === "no") numbers.push(19);
  if (["maybe", "no"].includes(answers.q20)) numbers.push(21);
  return numbers.sort((a, b) => a - b);
}

function questionAnswered(number, answers) {
  const field = `q${number}`;
  if (number === 7) return Boolean(answers.q7_games?.length);
  const value = answers[field];
  return Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== "";
}

export default function SurveyPage() {
  const content = getSurveyContent();
  const { data: me, isLoading: meLoading } = useMe();
  const surveyQuery = usePocketPlaySurvey({ enabled: Boolean(me) });
  const saveSurvey = useSavePocketPlaySurvey();
  const [answers, setAnswers] = React.useState({});
  const [savedAnswers, setSavedAnswers] = React.useState({});
  const [hydratedStamp, setHydratedStamp] = React.useState(null);

  const surveyData = surveyQuery.data;
  const response = surveyData?.response;
  const canEdit = Boolean(surveyData?.can_edit);
  const isCompleted = response?.status === "COMPLETED";
  const isDirty = JSON.stringify(answers) !== JSON.stringify(savedAnswers);

  React.useEffect(() => {
    if (!surveyData) return;
    const stamp = response?.updated_at || "empty";
    if (hydratedStamp === stamp) return;
    const serverAnswers = response?.answers || {};
    setAnswers(serverAnswers);
    setSavedAnswers(serverAnswers);
    setHydratedStamp(stamp);
  }, [surveyData, response, hydratedStamp]);

  React.useEffect(() => {
    if (!isDirty) return undefined;
    const beforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = content.ui.unsavedWarning;
      return content.ui.unsavedWarning;
    };
    const interceptLinks = (event) => {
      const link = event.target.closest?.("a[href]");
      if (!link || link.target === "_blank" || event.defaultPrevented) return;
      if (!window.confirm(content.ui.unsavedWarning)) event.preventDefault();
    };
    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("click", interceptLinks, true);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("click", interceptLinks, true);
    };
  }, [isDirty, content.ui.unsavedWarning]);

  const updateAnswer = (field, value, remove = []) => {
    setAnswers((current) => {
      const next = { ...current };
      if (value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) delete next[field];
      else next[field] = value;
      remove.forEach((key) => delete next[key]);
      return next;
    });
  };

  const currentGameAnswers = answers.q7_games || [];
  const currentGames = surveyData?.games || [];
  const gameMap = new Map(currentGames.map((game) => [game.id, game]));
  currentGameAnswers.forEach((game) => {
    if (!gameMap.has(game.game_id)) gameMap.set(game.game_id, { id: game.game_id, ...game });
  });
  const displayGames = [...gameMap.values()];

  const toggleGame = (gameId) => {
    const existing = currentGameAnswers.find((game) => game.game_id === gameId);
    updateAnswer("q7_games", existing
      ? currentGameAnswers.filter((game) => game.game_id !== gameId)
      : [...currentGameAnswers, { game_id: gameId, score: 5, comment: "" }]);
  };
  const updateGame = (gameId, field, value) => {
    updateAnswer("q7_games", currentGameAnswers.map((game) => game.game_id === gameId ? { ...game, [field]: value } : game));
  };

  const save = (action) => {
    saveSurvey.mutate({
      answers,
      action,
      expectedRevision: response?.revision || 0,
    }, {
      onSuccess: (data) => {
        const serverAnswers = data.response?.answers || {};
        setAnswers(serverAnswers);
        setSavedAnswers(serverAnswers);
        setHydratedStamp(data.response?.updated_at || "empty");
      },
    });
  };

  const reload = async () => {
    const result = await surveyQuery.refetch();
    const serverAnswers = result.data?.response?.answers || {};
    setAnswers(serverAnswers);
    setSavedAnswers(serverAnswers);
    setHydratedStamp(result.data?.response?.updated_at || "empty");
    saveSurvey.reset();
  };

  const formatDate = (value) => value ? new Intl.DateTimeFormat(content.language === "fa" ? "fa-IR" : "en", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "";
  const visibleNumbers = visibleQuestionNumbers(answers);
  const answeredCount = visibleNumbers.filter((number) => questionAnswered(number, answers)).length;
  const progress = Math.round((answeredCount / visibleNumbers.length) * 100);
  const disabled = !canEdit || saveSurvey.isPending;
  const saveError = saveSurvey.error;
  const stale = saveError?.response?.status === 409;

  if (meLoading) return <LoadingCard />;
  if (!me) {
    return (
      <main className={`flex flex-grow items-center justify-center px-4 py-20 ${content.language === "fa" ? "survey-persian" : ""}`} dir={content.direction}>
        <div className="max-w-lg rounded-[2rem] border border-white/10 bg-indigo-950/55 p-8 text-center shadow-2xl">
          <h1 className="survey-title-font text-3xl font-black text-white">{content.title}</h1>
          <p className="mt-4 leading-7 text-indigo-100/65">{content.ui.signInRequired}</p>
          <Link to="/login" className="mt-6 inline-flex rounded-full bg-gradient-to-r from-cyan-300 to-violet-400 px-7 py-3 font-black text-slate-950">{content.ui.signIn}</Link>
        </div>
      </main>
    );
  }
  if (surveyQuery.isPending) return <LoadingCard />;
  if (surveyQuery.isError) {
    return (
      <main className="flex flex-grow items-center justify-center px-4 py-20" dir={content.direction}>
        <div className="rounded-3xl border border-pink-300/25 bg-pink-500/10 p-8 text-center text-pink-100">
          <p>{content.ui.loadError}</p><button type="button" onClick={reload} className="mt-5 rounded-full bg-white px-5 py-2 font-bold text-slate-950">{content.ui.reload}</button>
        </div>
      </main>
    );
  }

  return (
    <main className={`relative flex-grow overflow-hidden pb-24 ${content.language === "fa" ? "survey-persian" : ""}`} dir={content.direction}>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -start-32 top-20 h-96 w-96 rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="absolute -end-36 top-[32rem] h-[30rem] w-[30rem] rounded-full bg-fuchsia-500/10 blur-3xl" />
      </div>

      <header className="relative mx-auto max-w-6xl px-4 pb-10 pt-14 text-center sm:pt-20">
        <p className="text-xs font-black uppercase tracking-[0.3em] text-cyan-200/80">{content.eyebrow}</p>
        <h1 className="survey-title-font mt-4 text-4xl font-black text-white sm:text-5xl">{content.title}</h1>
        <p className="mx-auto mt-5 max-w-3xl text-base leading-8 text-indigo-100/70 sm:text-lg">{content.intro}</p>
        <div className="mx-auto mt-8 max-w-3xl rounded-3xl border border-white/10 bg-white/[0.045] p-5 text-start backdrop-blur-xl">
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
            <span className="font-bold text-white">{content.ui.progress}: {progress}%</span>
            <span className="text-indigo-100/55">{answeredCount} / {visibleNumbers.length} {content.ui.answered}</span>
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-gradient-to-r from-cyan-300 via-violet-400 to-pink-400 transition-all" style={{ width: `${progress}%` }} /></div>
          <div className="mt-4 flex flex-wrap gap-2">
            {content.sections.map((title, index) => <button key={title} type="button" onClick={() => document.getElementById(`survey-section-${index + 1}`)?.scrollIntoView({ behavior: "smooth" })} className="rounded-full border border-white/10 bg-slate-950/35 px-3 py-1.5 text-xs font-bold text-indigo-100/65 transition hover:border-cyan-300/35 hover:text-white">{index + 1}</button>)}
          </div>
        </div>
        <div className={`mx-auto mt-5 max-w-3xl rounded-2xl border px-5 py-4 text-sm ${canEdit ? "border-emerald-300/20 bg-emerald-400/10 text-emerald-100" : "border-amber-300/25 bg-amber-300/10 text-amber-100"}`}>
          {canEdit
            ? surveyData.editable_until ? `${content.ui.deadline}: ${formatDate(surveyData.editable_until)}` : content.ui.unlimited
            : response ? content.ui.readOnly : content.ui.subscriptionRequired}
        </div>
      </header>

      {!canEdit && !response ? (
        <div className="relative mx-auto max-w-3xl px-4"><div className="rounded-3xl border border-white/10 bg-indigo-950/50 p-8 text-center text-indigo-100/65">{content.ui.noResponse}</div></div>
      ) : (
        <div className="relative mx-auto grid max-w-6xl gap-8 px-4">
          <Section index={0} title={content.sections[0]} description={content.sectionDescriptions[0]}>
            <Question number="1" title={content.questions.q1}><RadioChoices name="q1" options={content.options.q1} value={answers.q1} onChange={(value) => updateAnswer("q1", value)} disabled={disabled} /></Question>
            <Question number="2" title={content.questions.q2}><RadioChoices name="q2" options={content.options.q2} value={answers.q2} onChange={(value) => updateAnswer("q2", value)} disabled={disabled} /></Question>
          </Section>

          <Section index={1} title={content.sections[1]} description={content.sectionDescriptions[1]}>
            <Question number="3" title={content.questions.q3}><ScoreSlider min={0} max={10} value={answers.q3} onChange={(value) => updateAnswer("q3", value)} minLabel={content.slider.veryBad} maxLabel={content.slider.excellent} disabled={disabled} /></Question>
            <Question number="4" title={content.questions.q4}><ScoreSlider min={1} max={5} value={answers.q4} onChange={(value) => updateAnswer("q4", value)} minLabel={content.slider.veryConfusing} maxLabel={content.slider.veryClear} disabled={disabled} /></Question>
            <Question number="5" title={content.questions.q5}><ScoreSlider min={1} max={5} value={answers.q5} onChange={(value) => updateAnswer("q5", value)} minLabel={content.slider.veryHard} maxLabel={content.slider.veryEasy} disabled={disabled} /></Question>
            <Question number="6" title={content.questions.q6}>
              <RadioChoices name="q6" options={[...content.options.yesNo].reverse()} value={answers.q6} onChange={(value) => updateAnswer("q6", value, value === "yes" ? [] : ["q6_detail"])} disabled={disabled} />
              {answers.q6 === "yes" ? <div className="mt-4"><p className="text-start text-sm font-bold text-indigo-100/70">{content.questions.q6Detail}</p><TextAnswer value={answers.q6_detail} onChange={(value) => updateAnswer("q6_detail", value)} placeholder={content.ui.answerPlaceholder} disabled={disabled} /></div> : null}
            </Question>
          </Section>

          <Section index={2} title={content.sections[2]} description={content.sectionDescriptions[2]}>
            <Question number="7" title={content.questions.q7} hint={content.questions.q7Hint}>
              <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {displayGames.map((game) => {
                  const gameAnswer = currentGameAnswers.find((item) => item.game_id === game.id);
                  return (
                    <article key={game.id} className={`overflow-hidden rounded-3xl border transition ${gameAnswer ? "border-pink-300/45 bg-pink-500/10 shadow-xl shadow-pink-950/20" : "border-white/10 bg-white/[0.035]"}`}>
                      <label className={`block ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
                        <div className="relative h-28 overflow-hidden bg-indigo-950/60">
                          {game.cover_image ? <img src={game.cover_image} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-3xl">🎮</div>}
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 to-transparent" />
                          <input type="checkbox" className="absolute end-3 top-3 h-5 w-5 accent-pink-400" checked={Boolean(gameAnswer)} onChange={() => toggleGame(game.id)} disabled={disabled} aria-label={`${content.ui.tested}: ${game.title}`} />
                          <h3 className="absolute inset-x-4 bottom-3 text-start text-base font-black text-white">{game.title}</h3>
                        </div>
                      </label>
                      {gameAnswer ? (
                        <div className="border-t border-white/10 p-4">
                          <ScoreSlider min={0} max={10} value={gameAnswer.score} onChange={(value) => updateGame(game.id, "score", value)} minLabel={content.slider.scoreMin} maxLabel={content.slider.scoreMax} label={content.slider.gameScore} disabled={disabled} />
                          <label className="mt-4 block text-start text-xs font-bold text-indigo-100/65">{content.ui.gameComment}</label>
                          <TextAnswer rows={3} value={gameAnswer.comment || ""} onChange={(value) => updateGame(game.id, "comment", value)} placeholder={content.ui.gameCommentPlaceholder} disabled={disabled} />
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            </Question>
          </Section>

          <Section index={3} title={content.sections[3]} description={content.sectionDescriptions[3]}>
            <Question number="8" title={content.questions.q8} hint={content.questions.q8Hint}><TextAnswer value={answers.q8} onChange={(value) => updateAnswer("q8", value)} placeholder={content.ui.answerPlaceholder} disabled={disabled} /></Question>
            <Question number="9" title={content.questions.q9}><ScoreSlider min={1} max={5} value={answers.q9} onChange={(value) => updateAnswer("q9", value)} minLabel={content.slider.poorLaggy} maxLabel={content.slider.fastAccurate} disabled={disabled} /></Question>
            <Question number="10" title={content.questions.q10}><TextAnswer value={answers.q10} onChange={(value) => updateAnswer("q10", value)} placeholder={content.ui.answerPlaceholder} disabled={disabled} /></Question>
          </Section>

          <Section index={4} title={content.sections[4]} description={content.sectionDescriptions[4]}>
            <Question number="11" title={content.questions.q11}><RadioChoices name="q11" options={content.options.yesNo} value={answers.q11} onChange={(value) => updateAnswer("q11", value, value === "yes" ? [] : ["q12", "q13", "q14"])} disabled={disabled} /></Question>
            {answers.q11 === "yes" ? <>
              <Question number="12" title={content.questions.q12}><TextAnswer value={answers.q12} onChange={(value) => updateAnswer("q12", value)} placeholder={content.ui.answerPlaceholder} disabled={disabled} /></Question>
              <Question number="13" title={content.questions.q13}><ScoreSlider min={0} max={10} value={answers.q13} onChange={(value) => updateAnswer("q13", value)} minLabel={content.slider.notInterested} maxLabel={content.slider.veryInterested} disabled={disabled} /></Question>
              <Question number="14" title={content.questions.q14}><TextAnswer value={answers.q14} onChange={(value) => updateAnswer("q14", value)} placeholder={content.ui.answerPlaceholder} disabled={disabled} /></Question>
            </> : null}
          </Section>

          <Section index={5} title={content.sections[5]} description={content.sectionDescriptions[5]}>
            <Question number="15" title={content.questions.q15}><TextAnswer value={answers.q15} onChange={(value) => updateAnswer("q15", value)} placeholder={content.ui.answerPlaceholder} disabled={disabled} /></Question>
            <Question number="16" title={content.questions.q16} hint={content.ui.selectMultiple}>
              <CheckboxChoices options={content.options.q16} values={answers.q16} onChange={(value) => updateAnswer("q16", value, value.includes("other") ? [] : ["q16_other"])} disabled={disabled} />
              {answers.q16?.includes("other") ? <TextAnswer rows={2} value={answers.q16_other} onChange={(value) => updateAnswer("q16_other", value)} placeholder={content.ui.otherPlaceholder} disabled={disabled} /> : null}
            </Question>
          </Section>

          <Section index={6} title={content.sections[6]} description={content.sectionDescriptions[6]}>
            <Question number="17" title={content.questions.q17}><RadioChoices name="q17" options={content.options.yesNo} value={answers.q17} onChange={(value) => updateAnswer("q17", value, value === "no" ? [] : ["q18", "q19", "q19_amount"])} disabled={disabled} /></Question>
            {answers.q17 === "no" ? <Question number="18" title={content.questions.q18}><RadioChoices name="q18" options={content.options.yesNo} value={answers.q18} onChange={(value) => updateAnswer("q18", value, value === "no" ? [] : ["q19", "q19_amount"])} disabled={disabled} /></Question> : null}
            {answers.q17 === "no" && answers.q18 === "no" ? <Question number="19" title={content.questions.q19}>
              <RadioChoices name="q19" options={content.options.yesNo} value={answers.q19} onChange={(value) => updateAnswer("q19", value, value === "no" ? [] : ["q19_amount"])} disabled={disabled} />
              {answers.q19 === "no" ? <label className="mt-5 block text-start text-sm font-bold text-indigo-100/70">{content.questions.q19Amount}<span className="mt-2 flex items-center gap-3"><input type="number" min="0" step="1000" value={answers.q19_amount ?? ""} onChange={(event) => updateAnswer("q19_amount", event.target.value === "" ? undefined : Number(event.target.value))} disabled={disabled} className="w-full rounded-2xl border border-white/10 bg-slate-950/55 px-4 py-3 text-white outline-none focus:border-cyan-300/50" /><span>{content.ui.toman}</span></span></label> : null}
            </Question> : null}
          </Section>

          <Section index={7} title={content.sections[7]} description={content.sectionDescriptions[7]}>
            <Question number="20" title={content.questions.q20}><RadioChoices name="q20" options={content.options.q20} value={answers.q20} onChange={(value) => updateAnswer("q20", value, ["maybe", "no"].includes(value) ? [] : ["q21"])} disabled={disabled} /></Question>
            {["maybe", "no"].includes(answers.q20) ? <Question number="21" title={content.questions.q21}><TextAnswer value={answers.q21} onChange={(value) => updateAnswer("q21", value)} placeholder={content.ui.answerPlaceholder} disabled={disabled} /></Question> : null}
            <Question number="22" title={content.questions.q22}><RadioChoices name="q22" options={content.options.q22} value={answers.q22} onChange={(value) => updateAnswer("q22", value)} disabled={disabled} /></Question>
          </Section>

          <Section index={8} title={content.sections[8]} description={content.sectionDescriptions[8]}>
            <Question number="23" title={content.questions.q23} hint={`${content.ui.maxThree} ${answers.q23?.length || 0}/3`}>
              <CheckboxChoices options={content.options.q23} values={answers.q23} onChange={(value) => updateAnswer("q23", value, value.includes("other") ? [] : ["q23_other"])} disabled={disabled} max={3} />
              {answers.q23?.includes("other") ? <TextAnswer rows={2} value={answers.q23_other} onChange={(value) => updateAnswer("q23_other", value)} placeholder={content.ui.otherPlaceholder} disabled={disabled} /> : null}
            </Question>
            <Question number="24" title={content.questions.q24} hint={content.ui.selectMultiple}>
              <CheckboxChoices options={content.options.q24} values={answers.q24} onChange={(value) => updateAnswer("q24", value, value.includes("other") ? [] : ["q24_other"])} disabled={disabled} />
              {answers.q24?.includes("other") ? <TextAnswer rows={2} value={answers.q24_other} onChange={(value) => updateAnswer("q24_other", value)} placeholder={content.ui.otherPlaceholder} disabled={disabled} /> : null}
            </Question>
          </Section>

          <Section index={9} title={content.sections[9]} description={content.sectionDescriptions[9]}>
            {[25, 26, 27, 28].map((number) => <Question key={number} number={String(number)} title={content.questions[`q${number}`]}><TextAnswer value={answers[`q${number}`]} onChange={(value) => updateAnswer(`q${number}`, value)} placeholder={content.ui.answerPlaceholder} disabled={disabled} /></Question>)}
            <p className="py-3 text-center text-lg font-bold text-pink-100">{content.ui.thankYou}</p>
          </Section>

          <div className="sticky bottom-4 z-30 rounded-3xl border border-white/10 bg-slate-950/90 p-4 shadow-2xl shadow-black/50 backdrop-blur-2xl">
            <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
              <div className="text-center text-sm sm:text-start">
                <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                  <span className={`rounded-full px-3 py-1 text-xs font-black ${isCompleted ? "bg-emerald-400/15 text-emerald-200" : "bg-amber-300/15 text-amber-100"}`}>{isCompleted ? content.ui.completed : content.ui.draft}</span>
                  {isDirty ? <span className="text-amber-200">●</span> : null}
                  {!isDirty && response ? <span className="text-emerald-200">{content.ui.saved}</span> : null}
                </div>
                {response?.updated_at ? <p className="mt-1 text-xs text-indigo-100/45">{content.ui.lastSaved}: {formatDate(response.updated_at)}</p> : null}
              </div>
              {canEdit ? <div className="flex w-full gap-3 sm:w-auto">
                {!isCompleted ? <button type="button" onClick={() => save("draft")} disabled={!isDirty || saveSurvey.isPending} className="flex-1 rounded-2xl border border-white/15 bg-white/[0.06] px-5 py-3 text-sm font-black text-indigo-100 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40 sm:flex-none">{saveSurvey.isPending ? content.ui.saving : content.ui.saveDraft}</button> : null}
                <button type="button" onClick={() => save(isCompleted ? "update" : "complete")} disabled={(isCompleted && !isDirty) || saveSurvey.isPending} className="flex-1 rounded-2xl bg-gradient-to-r from-cyan-300 via-violet-400 to-pink-400 px-6 py-3 text-sm font-black text-slate-950 shadow-lg shadow-fuchsia-950/30 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40 sm:flex-none">{saveSurvey.isPending ? content.ui.saving : isCompleted ? content.ui.saveChanges : content.ui.finish}</button>
              </div> : null}
            </div>
            {saveSurvey.isError ? <div className="mt-3 rounded-2xl border border-pink-300/25 bg-pink-400/10 px-4 py-3 text-sm text-pink-100">{stale ? content.ui.staleError : saveError?.response?.data?.detail || content.ui.saveError}{stale ? <button type="button" onClick={reload} className="ms-3 underline underline-offset-4">{content.ui.reload}</button> : null}</div> : null}
          </div>
        </div>
      )}
    </main>
  );
}
